import { parseGIF, decompressFrames } from 'gifuct-js'
import { GIFEncoder, quantize, applyPalette } from 'gifenc'

/**
 * Crops an animated GIF while preserving animation.
 *
 * @param {string} gifUrl - data URL or object URL of the GIF
 * @param {object} cropData - { x, y, width, height } in *natural* image pixels
 * @param {object} [options]
 * @param {number} [options.outputWidth]  - desired output width  (defaults to cropData.width)
 * @param {number} [options.outputHeight] - desired output height (defaults to cropData.height)
 * @returns {Promise<{ blob: Blob, dataUrl: string }>}
 */
export async function cropAnimatedGif(gifUrl, cropData, options = {}) {
  // 1. Fetch the raw GIF bytes
  const response = await fetch(gifUrl)
  const buffer = await response.arrayBuffer()

  // 2. Parse & decompress every frame
  const gif = parseGIF(buffer)
  const frames = decompressFrames(gif, true) // true = build full patch per frame

  if (!frames.length) throw new Error('GIF has no frames')

  const outW = options.outputWidth || Math.round(cropData.width)
  const outH = options.outputHeight || Math.round(cropData.height)

  // 3. Create a GIF encoder for the output size
  const encoder = GIFEncoder()

  // Output canvas – used to crop & scale each composited frame
  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')

  // Full-size compositing canvas (logical screen)
  const fullCanvas = document.createElement('canvas')
  fullCanvas.width = gif.lsd.width
  fullCanvas.height = gif.lsd.height
  const fullCtx = fullCanvas.getContext('2d')

  // Reusable patch canvas (avoid creating one per frame)
  const patchCanvas = document.createElement('canvas')
  const patchCtx = patchCanvas.getContext('2d')

  // For disposal type 3 – "restore to previous"
  let savedImageData = null

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i]
    const { dims, patch, delay, disposalType } = frame

    // --- Save state BEFORE drawing if disposal is "restore to previous" (3) ---
    if (disposalType === 3) {
      savedImageData = fullCtx.getImageData(
        0,
        0,
        fullCanvas.width,
        fullCanvas.height
      )
    }

    // --- Draw this frame's patch onto the full-size compositing canvas ---
    patchCanvas.width = dims.width
    patchCanvas.height = dims.height
    const imageData = new ImageData(
      new Uint8ClampedArray(patch),
      dims.width,
      dims.height
    )
    patchCtx.putImageData(imageData, 0, 0)
    fullCtx.drawImage(patchCanvas, dims.left, dims.top)

    // --- Crop the region of interest into the output canvas ---
    // Fill with opaque white first so there are NEVER transparent holes.
    // Transparent holes would corrupt the output GIF encoding.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outW, outH)
    ctx.drawImage(
      fullCanvas,
      Math.round(cropData.x),
      Math.round(cropData.y),
      Math.round(cropData.width),
      Math.round(cropData.height),
      0,
      0,
      outW,
      outH
    )

    // Read the fully-opaque cropped pixels
    const croppedData = ctx.getImageData(0, 0, outW, outH)
    const { data: rgba } = croppedData

    // Quantise to 256-colour palette & map pixels (rgb565 = best opaque quality)
    const palette = quantize(rgba, 256, { format: 'rgb565' })
    const indexed = applyPalette(rgba, palette, 'rgb565')

    encoder.writeFrame(indexed, outW, outH, {
      palette,
      delay: delay || 100,
    })

    // --- Handle disposal for the NEXT frame ---
    if (disposalType === 2) {
      // Restore to background – clear the area this frame occupied
      fullCtx.clearRect(dims.left, dims.top, dims.width, dims.height)
    } else if (disposalType === 3 && savedImageData) {
      // Restore to previous – put back the saved canvas state
      fullCtx.putImageData(savedImageData, 0, 0)
      savedImageData = null
    }
    // disposalType 0 or 1 – do nothing, leave composite as-is
  }

  encoder.finish()

  const uint8 = encoder.bytes()
  const blob = new Blob([uint8], { type: 'image/gif' })
  const dataUrl = await blobToDataUrl(blob)

  return { blob, dataUrl }
}

/** Small helper – converts a Blob to a data-URL */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Detects whether a data-URL / object-URL points to an animated GIF
 * (more than one frame).
 */
export async function isAnimatedGif(url) {
  try {
    const res = await fetch(url)
    const buf = await res.arrayBuffer()
    const gif = parseGIF(buf)
    const frames = decompressFrames(gif, false)
    return frames.length > 1
  } catch {
    return false
  }
}
